import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';

import type { FormInstance } from '@arco-design/web-react';
import { ScreenSize } from '@dmp/types';

export const usePagination = <T>(options: { data: T[]; currentPage: number; pageSize: number }) => {
  const { data, currentPage, pageSize } = options;

  const currentPageData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return data.slice(start, end);
  }, [data, currentPage, pageSize]);

  return { currentPageData };
};

export const useGetWindowSize = () => {
  const [windowSize, setWindowSize] = useState(ScreenSize.Base);

  useEffect(() => {
    const update = () => {
      if (window.innerWidth >= 1536) setWindowSize(ScreenSize.TwoXl);
      else if (window.innerWidth >= 1280) setWindowSize(ScreenSize.Xl);
      else if (window.innerWidth >= 1024) setWindowSize(ScreenSize.Lg);
      else if (window.innerWidth >= 768) setWindowSize(ScreenSize.Md);
      else if (window.innerWidth >= 640) setWindowSize(ScreenSize.Sm);
      else setWindowSize(ScreenSize.Base);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const isWindowLargerThan = useCallback(
    (size: ScreenSize) => {
      const sizeMap = {
        [ScreenSize.TwoXl]: 1536,
        [ScreenSize.Xl]: 1280,
        [ScreenSize.Lg]: 1024,
        [ScreenSize.Md]: 768,
        [ScreenSize.Sm]: 640,
        [ScreenSize.Base]: 0,
      };

      return sizeMap[windowSize] > sizeMap[size];
    },
    [windowSize]
  );

  return { windowSize, isWindowLargerThan };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useFormFieldTrim = (form: FormInstance<any>) => {
  const trimOnBlur = useCallback(
    (fieldName: string) => {
      const value = form.getFieldValue(fieldName);
      if (typeof value === 'string') {
        form.setFieldValue(fieldName, value.trim());
      }
    },
    [form]
  );

  return { trimOnBlur };
};

export const useBrowserIdleCheck = (options: { onIdle: () => void; onActive: () => void }) => {
  // check if the user is idle every 10 min
  useIdleTimer({
    timeout: 1000 * 60 * 10,
    onIdle() {
      options.onIdle();
    },
    onActive() {
      options.onActive();
    },
  });
};
